import classNames from "classnames";
import {
	ActionListDivider,
	ActionListItem,
	Anchor,
	Badge,
	DropOverActionList,
	Icon
} from "client/Components";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	Fragment,
	ReactElement,
	RefObject,
	useMemo,
	useRef,
	useState
} from "react";
import { Guid } from "client_legacy/ndaw/guid";
import styles from "./TileTabSelector.module.less";

type TileTabBase = {
	uniqueId: Guid;
	name: string;
	groupName?: string;
};

export type TileTabProps = TileTabBase & (
	{ suffix?: string; count?: undefined; }
	| { count?: number; suffix?: undefined; }
);

export type TileTabSelectorProps = {
	value: Guid | undefined;
	onChange(value: Guid): void;
	tabs: readonly TileTabProps[];
	tileId: number;
	tileName: string;
};

export const TileTabSelector = (props: TileTabSelectorProps): ReactElement | null => {
	const {
		value,
		onChange,
		tabs,
		tileId,
		tileName,
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLAnchorElement | null> = useRef<HTMLAnchorElement>(null);

	const activeTab: TileTabProps | undefined = tabs.find(x => x.uniqueId === value);
	const hasMultipleTabs: boolean = tabs.length > 1;

	const suffix = useMemo(
		(): string | undefined => {
			if (isNil(activeTab)) {
				return undefined;
			}

			const { suffix, count } = activeTab;

			if (isNotNil(suffix)) {
				return suffix;
			}

			if (isNil(count) || count === 0) {
				return undefined;
			}

			return count > 99
				? "99+"
				: count.toString(10);
		},
		[activeTab]
	);

	if (tabs.length === 0) {
		return null;
	}

	const clickHandler = (): void => {
		setIsOpen(true);
	};

	const renderTabs = (): ReactElement => {

		let lastGroupName: string | undefined = undefined;

		return (
			<>
				{tabs.map((x, i):ReactElement => {
					const isSelected: boolean = value === x.uniqueId;
					const shouldAddDivider = i !== 0 && x.groupName !== lastGroupName;
					lastGroupName = x.groupName;

					const count: ReactElement | undefined = isNil(x.count) || x.count === 0
						? undefined
						: (
							<Badge
								amount={x.count}
								isActive={isSelected}
								trackingId={generateTrackingId(x.uniqueId, "|count")}
							/>
						);

					return (
						<Fragment key={x.uniqueId}>
							{shouldAddDivider && (
								<ActionListDivider />
							)}

							<ActionListItem
								isActive={isSelected}
								rightIcon={isSelected && isNil(count) ? "check" : undefined}
								onClick={() => onChange(x.uniqueId)}
								text={x.name}
								trackingId={generateTrackingId("tab", `|${x.uniqueId}`)}
								suffix={count}
							/>
						</Fragment>
					);
				})}
			</>
		);
	};

	return (
		<>
			<Anchor
				trackingId={generateTrackingId(tileId.toString(), "|tab-selector")}
				className={classNames(
					styles.root,
					{
						[styles.root_hasSingleTab]: tabs.length === 1
					}
				)}
				onClick={hasMultipleTabs ? clickHandler : undefined}
				ref={ref}
			>
				{activeTab && (
					<>
						<h4 className={styles.text}>
							{activeTab.name}
						</h4>
						{suffix && (
							<h4 className={styles.suffix}>
								({suffix})
							</h4>
						)}
					</>
				)}

				{hasMultipleTabs && (
					<h4 className={styles.caret}>
						<Icon value="chevron-down" size="small" />
					</h4>
				)}
			</Anchor>

			{(isOpen && ref.current) && (
				<DropOverActionList
					target={ref.current}
					onClose={() => setIsOpen(false)}
					isHeightConstrainted
					trackingId={generateTrackingId(tileId.toString(), "|tab-selector__dropover")}
					menuTitle={tileName}
				>
					{renderTabs()}
				</DropOverActionList>
			)}
		</>
	);
};
TileTabSelector.displayName = "TileTabSelector";
